/* =============================================================================
   File: assets/ak-amazon-gallery.js
   
   Features:
   - Amazon-style modal with centered dialog and grey backdrop
   - Closes on Outside Click (Backdrop) or X button
   - Enlarged Square Magnifier (350px)
   - Wheel/Trackpad scroll navigation
   - Keyboard support (Esc, Arrows)
   ============================================================================= */

class AKAmazonGallery {
  constructor(root) {
    this.root = root;
    this.sectionId = root.dataset.section || '';
    this.mediaWrapper = root.querySelector('.product__media-wrapper');
    this.dataScript = document.getElementById(`AkAmazonGalleryData-${this.sectionId}`);
    this.modal = document.getElementById(`AkAmazonGallery-${this.sectionId}`);

    if (!this.sectionId || !this.mediaWrapper || !this.dataScript || !this.modal) return;

    this.data = this.parseGalleryData(this.dataScript);
    if (!this.data || !Array.isArray(this.data.images) || !this.data.images.length) return;

    // Elements
    this.dialog = this.modal.querySelector('.ak-amazon-gallery-modal__dialog');
    this.mainImage = this.modal.querySelector('[data-ak-gallery-main-image]');
    this.imageWrap = this.modal.querySelector('.ak-amazon-gallery-modal__image-wrap');
    this.stage = this.modal.querySelector('.ak-amazon-gallery-modal__stage');
    this.thumbsWrap = this.modal.querySelector('[data-ak-gallery-thumbs]');
    this.prevButton = this.modal.querySelector('[data-ak-gallery-prev]');
    this.nextButton = this.modal.querySelector('[data-ak-gallery-next]');
    this.closeButtons = this.modal.querySelectorAll('[data-ak-gallery-close]');

    // Magnifier Settings (Enlarged)
    this.magnifierSize = 400; 
    this.magnifierZoom = 2.0;

    // Wheel/Scroll Navigation Settings
    this.wheelDeltaAccumulator = 0;
    this.wheelThreshold = 60;
    this.wheelCooldownMs = 220;
    this.lastWheelNavigationAt = 0;

    this.currentIndex = 0;
    this.isOpen = false;

    this.init();
  }

  parseGalleryData(script) {
    try {
      return JSON.parse(script.textContent);
    } catch (e) {
      console.error('AK Gallery JSON Error:', e);
      return null;
    }
  }

  init() {
    this.renderThumbs();
    this.createMagnifier();
    this.bindEvents();
  }

  bindEvents() {
    // Open modal on product image click
    this.mediaWrapper.addEventListener('click', (e) => this.handleMediaClick(e), true);

    // Navigation buttons
    this.prevButton?.addEventListener('click', () => this.handlePrev());
    this.nextButton?.addEventListener('click', () => this.handleNext());

    // Close on X buttons
    this.closeButtons.forEach(btn => btn.addEventListener('click', () => this.close()));

    // --- OUTSIDE CLICK LOGIC ---
    this.modal.addEventListener('click', (e) => {
      // If the click target is exactly the modal container (the backdrop area), close it.
      if (e.target === this.modal) {
        this.close();
      }
    });

    // Prevent clicks inside the white box from closing the modal
    this.dialog.addEventListener('click', (e) => e.stopPropagation());

    // Magnifier and Wheel
    this.imageWrap.addEventListener('mousemove', (e) => this.handleMouseMove(e));
    this.imageWrap.addEventListener('mouseleave', () => this.hideMagnifier());
    this.stage.addEventListener('wheel', (e) => this.handleWheel(e), { passive: false });
    this.mainImage.addEventListener('load', () => this.updateMagnifierImg());

    window.addEventListener('resize', () => this.hideMagnifier());
  }

  createMagnifier() {
    this.magnifier = document.createElement('div');
    this.magnifier.className = 'ak-amazon-gallery-modal__magnifier';
    this.magnifier.style.width = `${this.magnifierSize}px`;
    this.magnifier.style.height = `${this.magnifierSize}px`;
    this.imageWrap.appendChild(this.magnifier);
  }

  handleMediaClick(e) {
    if (e.target.closest('.thumbnail-list, .slider-button')) return;
    const trigger = e.target.closest('.product__media-item, modal-opener');
    if (!trigger) return;

    e.preventDefault();
    e.stopPropagation();

    const img = trigger.querySelector('img');
    const index = this.data.images.findIndex(item => 
      String(img?.src).includes(String(item.src).split('?')[0])
    );

    this.open(index >= 0 ? index : 0);
  }

  renderThumbs() {
    this.thumbsWrap.innerHTML = '';
    this.data.images.forEach((img, idx) => {
      const btn = document.createElement('button');
      btn.className = 'ak-amazon-gallery-modal__thumb';
      btn.innerHTML = `<img src="${img.thumb}" alt="thumb">`;
      btn.addEventListener('click', () => this.setActiveImage(idx));
      this.thumbsWrap.appendChild(btn);
    });
  }

  setActiveImage(index) {
    this.currentIndex = index;
    const item = this.data.images[index];
    this.mainImage.src = item.src;
    
    const thumbs = this.thumbsWrap.querySelectorAll('.ak-amazon-gallery-modal__thumb');
    thumbs.forEach((t, i) => t.classList.toggle('is-active', i === index));
    thumbs[index]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    
    this.updateMagnifierImg();
    this.hideMagnifier();
  }

  updateMagnifierImg() {
    if (this.magnifier) {
      this.magnifier.style.backgroundImage = `url('${this.mainImage.src}')`;
    }
  }

  handleMouseMove(e) {
    if (!this.isOpen || window.innerWidth <= 989) return;

    const wrapRect = this.imageWrap.getBoundingClientRect();
    const imgRect = this.mainImage.getBoundingClientRect();

    const mouseX = e.clientX - imgRect.left;
    const mouseY = e.clientY - imgRect.top;

    if (mouseX < 0 || mouseX > imgRect.width || mouseY < 0 || mouseY > imgRect.height) {
      this.hideMagnifier();
      return;
    }

    this.magnifier.classList.add('is-visible');

    const zoom = this.magnifierZoom;
    let lensX = (e.clientX - wrapRect.left) - (this.magnifierSize / 2);
    let lensY = (e.clientY - wrapRect.top) - (this.magnifierSize / 2);

    const posX = (mouseX * zoom) - (this.magnifierSize / 2);
    const posY = (mouseY * zoom) - (this.magnifierSize / 2);

    this.magnifier.style.left = `${lensX}px`;
    this.magnifier.style.top = `${lensY}px`;
    this.magnifier.style.backgroundSize = `${imgRect.width * zoom}px ${imgRect.height * zoom}px`;
    this.magnifier.style.backgroundPosition = `-${posX}px -${posY}px`;
  }

  hideMagnifier() {
    this.magnifier?.classList.remove('is-visible');
  }

  handleWheel(e) {
    if (!this.isOpen) return;
    e.preventDefault();

    const now = Date.now();
    if (now - this.lastWheelNavigationAt < this.wheelCooldownMs) return;

    this.wheelDeltaAccumulator += e.deltaY;
    if (Math.abs(this.wheelDeltaAccumulator) < this.wheelThreshold) return;

    if (this.wheelDeltaAccumulator > 0) this.handleNext();
    else this.handlePrev();

    this.lastWheelNavigationAt = now;
    this.wheelDeltaAccumulator = 0;
  }

  open(index) {
    this.isOpen = true;
    this.setActiveImage(index);
    this.modal.hidden = false;
    this.wheelDeltaAccumulator = 0;
    
    document.body.classList.add('overflow-hidden');
    this._keydownHandler = (e) => this.handleKeydown(e);
    document.addEventListener('keydown', this._keydownHandler);
  }

  close() {
    this.isOpen = false;
    this.modal.hidden = true;
    document.body.classList.remove('overflow-hidden');
    document.removeEventListener('keydown', this._keydownHandler);
    this.hideMagnifier();
  }

  handlePrev() {
    const idx = (this.currentIndex === 0) ? this.data.images.length - 1 : this.currentIndex - 1;
    this.setActiveImage(idx);
  }

  handleNext() {
    const idx = (this.currentIndex === this.data.images.length - 1) ? 0 : this.currentIndex + 1;
    this.setActiveImage(idx);
  }

  handleKeydown(e) {
    if (e.key === 'Escape') this.close();
    if (e.key === 'ArrowLeft') this.handlePrev();
    if (e.key === 'ArrowRight') this.handleNext();
  }
}

function initAKGallery() {
  document.querySelectorAll('[data-ak-gallery-root]:not([data-initialized])').forEach(root => {
    root.dataset.initialized = 'true';
    new AKAmazonGallery(root);
  });
}

document.addEventListener('DOMContentLoaded', initAKGallery);
if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', initAKGallery);
}